import { AbstractService } from "cruzo";
import { Lang, trs } from "site/translate";
import { parseSelectZeptoExtras } from "site/services/zepto-editor";
import { playerService } from "site/services/player.service";
import {
  clearLessonProgressFromStorage,
  readLessonProgressFromStorage,
  writeLessonProgressToStorage,
} from "site/services/lesson-progress-storage";
import { patchAppSettings, readAppSettings } from "site/services/settings-storage";
import { pushUserSettingsIfAuthorized, deleteLessonProgressOnServer } from "site/services/user-sync.service";

export interface Step { descriptor: StepDescriptor, data: any, subIndex: number, index: number }
export interface Stage { descriptor: StageDescriptor, index: number }

export interface StageDescriptor { description?: string, key: string, button?: string }

export interface StepDescriptor {
  key: 'dictionaries' | 'dialogs' | 'groups' | 'single' | 'view' | 'select',
  stages?: StageDescriptor[],
  order: number,
}

export const stepDescriptors: StepDescriptor[] = [
  { key: 'groups', stages: [{ description: 'Выберите правильный вариант', key: 'choose', button: 'Далее' }], order: 5 },
  { key: 'select', stages: [{ description: 'Выберите правильную форму', key: 'choose', button: 'Далее' }], order: 3 },
  { key: 'view', stages: [{ description: 'Прочитайте предложение', key: 'read', button: 'Далее' }], order: 4 },
  {
    key: 'dictionaries',
    stages: [
      { key: 'rememberWords', description: 'Запомните слова', button: 'Далее' },
      { key: 'checkWords', description: 'Выберите подходящее слово', button: 'Далее' }
    ],
    order: 1,
  },
  { key: 'dialogs', stages: [{ description: 'Прочитайте диалог', key: 'read', button: 'Далее' }], order: 2 },
  { key: 'single', stages: [{ description: 'Составьте предложения', key: 'choose', button: 'Далее' }], order: 6 },
]

stepDescriptors.sort((a,b) => a.order - b.order)

export type Exercise = {
  id: number;
  title_arabic?: string;
  title_russian?: string;
  type_icon_text?: string;
  type?: string;
  zepto: any[];
  audio: Record<string, string>;
};

enum TYPES {
  'new_word' = 1,
  'sentence' = 2,
  'task' = 3,
  'dialog' = 4,
  'build' = 5,
  'select' = 6
}

const DIC_SIZE = 4
const DIALOG_SIZE = 1000

export interface LessonRuleFormulaRow {
  formula: string;

  example?: string;
}

export interface LessonRulePayload {
  ru: string;
  formulaRows: LessonRuleFormulaRow[];

  titleRu?: string;

  refTitleAr?: string;

  seeAlsoRuleId?: string;

  seeAlsoTitle?: string;
}

export function cloneLessonRulePayload(r: LessonRulePayload): LessonRulePayload {
  return {
    ru: r.ru,
    formulaRows: r.formulaRows.map((row) => ({ ...row })),
    ...(r.titleRu ? { titleRu: r.titleRu } : {}),
    ...(r.refTitleAr ? { refTitleAr: r.refTitleAr } : {}),
    ...(r.seeAlsoRuleId ? { seeAlsoRuleId: r.seeAlsoRuleId } : {}),
    ...(r.seeAlsoTitle ? { seeAlsoTitle: r.seeAlsoTitle } : {}),
  };
}

export function lessonRuleModalTitle(p: LessonRulePayload | null | undefined): string {
  if (!p) return "Правило";
  const t = p.refTitleAr || p.titleRu || p.seeAlsoTitle;
  return t || "Правило";
}

export function lessonRuleModalTitleDir(p: LessonRulePayload | null | undefined): "rtl" | "ltr" {
  if (!p) return "ltr";
  if (p.titleRu && !p.refTitleAr) return "ltr";
  if (p.refTitleAr || p.seeAlsoTitle) return "rtl";
  return "ltr";
}

export function lessonRuleModalTitleLang(p: LessonRulePayload | null | undefined): "ar" | "ru" {
  return lessonRuleModalTitleDir(p) === "rtl" ? "ar" : "ru";
}

class AppService extends AbstractService {
  data = null as any;

  arabicRules: Record<string, unknown> | null = null;

  trs$ = this.newRx(trs);
  currentLanguage$ = this.newRx<Lang>(Lang.ru);

  exercise$ = this.newRx<Exercise>(null);
  lessonBookTitle$ = this.newRx('');

  lessonBookListHref$ = this.newRx('');

  lessonProgressBookId = "";
  lessonProgressExerciseId = "";
  curStage$ = this.newRx<Stage>(null);
  curStep$ = this.newRx<Step>(null);
  curStepItemIndex$ = this.newRx<number>(1);
  lessonCompleted$ = this.newRx<boolean>(false);
  autoPlayOnCorrect$ = this.newRx<boolean>(false);

  subscriptionAccessRevision$ = this.newRx(0);

  sessionPermissions$ = this.newRx<string[]>([]);

  subscriptionAccessLoading$ = this.newRx(false);

  private subscriptionAccessLoadingListeners = new Set<(loading: boolean) => void>();

  lessonProgressRevision$ = this.newRx(0);

  grammarLessonRule$ = this.newRx<LessonRulePayload | null>(null);

  grammarRuleParentStack$ = this.newRx<LessonRulePayload[]>([]);

  constructor() {
    super();
    const settings = readAppSettings();
    this.autoPlayOnCorrect$.update(settings.autoPlayOnCorrect);
    this.autoPlayOnCorrect$.setPostUpdate(() => {
      patchAppSettings({ autoPlayOnCorrect: this.autoPlayOnCorrect$.actual });
    });
  }

  applySettingsFromStorage(): void {
    const settings = readAppSettings();
    this.autoPlayOnCorrect$.update(settings.autoPlayOnCorrect);
  }

  setSubscriptionAccessLoading(loading: boolean): void {
    const next = !!loading;
    if (this.subscriptionAccessLoading$.actual === next) {
      return;
    }
    this.subscriptionAccessLoading$.update(next);
    for (const fn of this.subscriptionAccessLoadingListeners) {
      fn(next);
    }
  }

  notifySubscriptionAccessChanged(): void {
    const cur = this.subscriptionAccessRevision$.actual;
    const n = typeof cur === "number" && Number.isFinite(cur) ? cur : 0;
    this.subscriptionAccessRevision$.update(n + 1);
  }

  notifyLessonProgressChanged(): void {
    const cur = this.lessonProgressRevision$.actual;
    const n = typeof cur === "number" && Number.isFinite(cur) ? cur : 0;
    this.lessonProgressRevision$.update(n + 1);
  }

  resetGrammarRuleBackStack() {
    this.grammarRuleParentStack$.update([]);
  }

  pushGrammarRuleParentSnapshot() {
    const cur = this.grammarLessonRule$.actual;
    if (!cur) return;
    const stack = [...this.grammarRuleParentStack$.actual, cloneLessonRulePayload(cur)];
    this.grammarRuleParentStack$.update(stack);
  }

  popGrammarRuleParent(): LessonRulePayload | null {
    const stack = this.grammarRuleParentStack$.actual;
    if (stack.length === 0) return null;
    const parent = stack[stack.length - 1]!;
    this.grammarRuleParentStack$.update(stack.slice(0, -1));
    return cloneLessonRulePayload(parent);
  }

  private normalizeLessonRule(raw: unknown): LessonRulePayload | null {
    if (raw === null || raw === undefined) return null;
    if (typeof raw === "string") {
      const ru = raw;
      return ru ? { ru, formulaRows: [] as LessonRuleFormulaRow[] } : null;
    }
    if (typeof raw === "object") {
      const o = raw as Record<string, unknown>;
      const ru = typeof o.ru === "string" ? o.ru : "";

      let formulaRows: LessonRuleFormulaRow[] = [];

      const blocks = o.formulaBlocks;
      if (Array.isArray(blocks)) {
        for (const item of blocks) {
          if (!item || typeof item !== "object") continue;
          const row = item as Record<string, unknown>;
          const formula = typeof row.formula === "string" ? row.formula : "";
          if (!formula) continue;
          const ex = typeof row.example === "string" ? row.example : "";
          formulaRows.push(ex ? { formula, example: ex } : { formula });
        }
      }

      const f = o.formulas;
      if (formulaRows.length === 0 && Array.isArray(f)) {
        const examples = Array.isArray(o.formulaExamples) ? (o.formulaExamples as unknown[]) : [];
        f.forEach((item, i) => {
          if (typeof item !== "string") return;
          const formula = item;
          if (!formula) return;
          const exRaw = examples[i];
          const ex = typeof exRaw === "string" ? exRaw : "";
          formulaRows.push(ex ? { formula, example: ex } : { formula });
        });
      }

      if (!ru && formulaRows.length === 0) return null;

      const seeAlsoRuleId =
        typeof o.seeAlsoRuleId === "string" && o.seeAlsoRuleId ? o.seeAlsoRuleId : undefined;
      const seeAlsoTitle =
        typeof o.seeAlsoTitle === "string" && o.seeAlsoTitle ? o.seeAlsoTitle : undefined;
      const refTitleAr =
        typeof o.refTitleAr === "string" && o.refTitleAr ? o.refTitleAr : undefined;
      const titleRu =
        typeof o.titleRu === "string" && o.titleRu ? o.titleRu : undefined;

      const base: LessonRulePayload = { ru, formulaRows };
      if (seeAlsoRuleId) base.seeAlsoRuleId = seeAlsoRuleId;
      if (seeAlsoTitle) base.seeAlsoTitle = seeAlsoTitle;
      if (refTitleAr) base.refTitleAr = refTitleAr;
      if (titleRu) base.titleRu = titleRu;
      return base;
    }
    return null;
  }

  getLessonRule(ruleId: string | null | undefined): LessonRulePayload | null {
    if (!ruleId || !this.arabicRules) return null;
    return this.normalizeLessonRule(this.arabicRules[ruleId]);
  }

  setLessonProgressContext(bookId: string, exerciseId: string) {
    this.lessonProgressBookId = bookId
    this.lessonProgressExerciseId = exerciseId
  }

  persistLessonProgressSnapshot(): void {
    const bookId = this.lessonProgressBookId;
    const exerciseId = this.lessonProgressExerciseId;
    if (!bookId || !exerciseId || !this.data) {
      return;
    }

    if (this.lessonCompleted$.actual) {
      writeLessonProgressToStorage(bookId, exerciseId, {
        stepKey: "",
        stageKey: "",
        subIndex: 0,
        stageIndex: 0,
        curStepItemIndex: 0,
        lessonCompleted: true,
        progressPercent: 100,
      });
      this.notifyLessonProgressChanged();
      return;
    }

    const curStep = this.curStep$.actual;
    const curStage = this.curStage$.actual;
    if (!curStep?.descriptor || !curStage?.descriptor) {
      return;
    }

    writeLessonProgressToStorage(bookId, exerciseId, {
      stepKey: curStep.descriptor.key,
      stageKey: curStage.descriptor.key,
      subIndex: curStep.subIndex,
      stageIndex: curStage.index,
      curStepItemIndex: this.curStepItemIndex$.actual,
      lessonCompleted: false,
      progressPercent: this.getLessonProgressPercent(),
    });

    this.notifyLessonProgressChanged();
  }

  restoreLessonProgress(): boolean {
    const bookId = this.lessonProgressBookId;
    const exerciseId = this.lessonProgressExerciseId;
    if (!bookId || !exerciseId || !this.data) {
      return false;
    }

    const saved = readLessonProgressFromStorage(bookId, exerciseId);
    if (!saved) {
      return false;
    }

    if (saved.lessonCompleted) {
      this.lessonCompleted$.update(true);
      this.curStep$.update(null);
      this.curStage$.update(null);
      this.curStepItemIndex$.update(0);
      return true;
    }

    const step = this.rebuildStepFromSaved(this.data, saved.stepKey, saved.subIndex);
    if (!step) {
      return false;
    }

    const stage = this.rebuildStageFromSaved(step.descriptor, saved.stageKey, saved.stageIndex);
    if (!stage?.descriptor) {
      return false;
    }

    this.lessonCompleted$.update(false);
    this.curStep$.update(step);
    this.curStage$.update(stage);
    const cii = Math.max(0, Math.floor(saved.curStepItemIndex));
    this.curStepItemIndex$.update(cii > 0 ? cii : 1);
    return true;
  }

  private rebuildStepFromSaved(data: any, stepKey: string, subIndex: number): Step | null {
    if (!data || typeof stepKey !== "string" || !stepKey) {
      return null;
    }
    const stepIndex = stepDescriptors.findIndex((d) => d.key === stepKey);
    if (stepIndex < 0) {
      return null;
    }
    const descriptor = stepDescriptors[stepIndex];
    const arr = data[descriptor.key];
    if (!Array.isArray(arr)) {
      return null;
    }
    const si = Math.floor(subIndex);
    if (si < 0 || si >= arr.length || !this.hasStepItem(arr[si])) {
      return null;
    }
    return {
      data: arr,
      descriptor,
      index: stepIndex,
      subIndex: si,
    };
  }

  private rebuildStageFromSaved(descriptor: StepDescriptor, stageKey: string, stageIndex: number): Stage | null {
    const stages = descriptor.stages;
    if (!stages?.length) {
      return null;
    }
    let idx = typeof stageKey === "string" && stageKey ? stages.findIndex((s) => s.key === stageKey) : -1;
    if (idx < 0) {
      idx = Math.min(Math.max(0, Math.floor(stageIndex)), stages.length - 1);
    } else {
      idx = Math.min(idx, stages.length - 1);
    }
    const st = stages[idx];
    if (!st) {
      return null;
    }
    return { index: idx, descriptor: st };
  }

  resetLessonProgressOnLogout(): void {
    this.lessonProgressBookId = "";
    this.lessonProgressExerciseId = "";
    this.notifyLessonProgressChanged();
  }

  completeCurrentStage(): void {
    if (!this.data || this.lessonCompleted$.actual) {
      return;
    }

    const step = this.curStep$.actual;
    const stage = this.curStage$.actual;
    if (!step?.descriptor || !stage?.descriptor) {
      return;
    }

    const stages = step.descriptor.stages;
    if (stages && stage.index < stages.length - 1) {
      this.updateStage();
      return;
    }

    this.updateStep();
  }

  clearExerciseSession() {
    this.exercise$.update(null);
    this.data = null;
    this.arabicRules = null;
    this.grammarLessonRule$.update(null);
    this.grammarRuleParentStack$.update([]);
    this.curStep$.update(null);
    this.curStage$.update(null);
    this.curStepItemIndex$.update(1);
    this.lessonCompleted$.update(false);
    this.lessonBookTitle$.update('');
    this.lessonBookListHref$.update('');
    this.lessonProgressBookId = "";
    this.lessonProgressExerciseId = "";
  }

  getData(zepto: any[]) {
    const normalizeText = (value: any) => {
      if (typeof value === "string") {
        return value.includes("\n") ? value.split("\n") : [value];
      }
      if (Array.isArray(value) && value.every((item) => typeof item === "string")) {
        return value;
      }
      return [""];
    };

    const normalizeTranslate = (value: any) => {
      if (Array.isArray(value)) {
        const ru = value[0];
        return typeof ru === "string" ? ru : "";
      }
      return "";
    };

    const dictionaries: any[] = [];
    let curDictionariesCursor = -1;

    const dialogs: any[] = [];
    let curDialogCursor = -1;

    const groups: any[] = [];
    let curTaskCursor = 0;

    const select = [];

    const view = [];

    const single = [];

    let prevType = null

    for (let index = 0; index < zepto.length; index++) {
      const element = zepto[index];
      const type = element[0];
      const content = element[1];
      const id = element[2];
      const text = normalizeText(content?.[0]);
      const translate = normalizeTranslate(content?.[1]);
      switch (type) {
        case TYPES.new_word:
          if (prevType !== TYPES.new_word || dictionaries[curDictionariesCursor].length === DIC_SIZE) curDictionariesCursor++
          dictionaries[curDictionariesCursor] = dictionaries[curDictionariesCursor] || [];
          dictionaries[curDictionariesCursor].push({ text, translate, id, type })
          break;
        case TYPES.dialog:
          if (prevType !== TYPES.dialog || dialogs[curDialogCursor].length === DIALOG_SIZE) curDialogCursor++
          dialogs[curDialogCursor] = dialogs[curDialogCursor] || [];
          dialogs[curDialogCursor].push({ text, translate, id, type })
          break;
        case TYPES.build:
          single.push({ text, translate, id, type })
          break;
        case TYPES.sentence:
          view.push({ text, translate, id, type })
          break;
        case TYPES.select: {
          const extras = parseSelectZeptoExtras(content);
          select.push({
            text,
            translate,
            id,
            type,
            ...(extras.ruleId ? { ruleId: extras.ruleId } : {}),
            ...(extras.alwaysShowTranslate ? { alwaysShowTranslate: true } : {}),
          });
          break;
        }
        case TYPES.task:
          groups[curTaskCursor] = groups[curTaskCursor] || []
          groups[curTaskCursor].push({ text, translate, id, type })
          if (groups[curTaskCursor].length === 5) curTaskCursor++
          break;
      }

      prevType = type
    }

    let dicIndex = 0;
    while (dicIndex < dictionaries.length - 1) {
      const currentChunk = dictionaries[dicIndex] || [];
      const nextChunk = dictionaries[dicIndex + 1] || [];

      while (currentChunk.length < DIC_SIZE && nextChunk.length > 0) {
        currentChunk.push(nextChunk.shift());
      }

      dictionaries[dicIndex] = currentChunk;

      if (nextChunk.length === 0) {
        dictionaries.splice(dicIndex + 1, 1);
        continue;
      }

      dictionaries[dicIndex + 1] = nextChunk;
      dicIndex++;
    }

    return { dialogs, single, dictionaries, groups, view, select }
  }

  findNextStep(data: any, curStepIndex: number, curSubIndex: number): Step | null {
    const hasStepContent = (stepData: any) => {
      if (!Array.isArray(stepData) || stepData.length === 0) {
        return false;
      }

      return stepData.some((item) => {
        if (Array.isArray(item)) {
          return item.length > 0;
        }

        return !!item;
      });
    };

    const hasStepItem = (item: any) => {
      if (Array.isArray(item)) {
        return item.length > 0;
      }
      return !!item;
    };

    const startStepIndex = Math.max(0, curStepIndex);

    for (let stepIndex = startStepIndex; stepIndex < stepDescriptors.length; stepIndex++) {
      const descriptor = stepDescriptors[stepIndex];
      const curStepData = data?.[descriptor.key];

      if (!hasStepContent(curStepData)) {
        continue;
      }

      const startSubIndex = stepIndex === curStepIndex ? curSubIndex + 1 : 0;
      for (let subIndex = startSubIndex; subIndex < curStepData.length; subIndex++) {
        if (!hasStepItem(curStepData[subIndex])) {
          continue;
        }

        return {
          data: curStepData,
          descriptor,
          index: stepIndex,
          subIndex,
        };
      }
    }

    return null;
  }

  findNextStage(curDescriptor: StepDescriptor, curStageIndex: number): Stage {
    const descriptor = curDescriptor.stages[++curStageIndex]

    return { index: curStageIndex, descriptor }
  }

  findPrevStep(data: any, curStepIndex: number, curSubIndex: number): Step | null {
    if (!data) {
      return null;
    }

    const startStepIndex = Math.min(Math.max(0, curStepIndex), stepDescriptors.length - 1);

    for (let stepIndex = startStepIndex; stepIndex >= 0; stepIndex--) {
      const descriptor = stepDescriptors[stepIndex];
      const curStepData = data?.[descriptor.key];
      if (!Array.isArray(curStepData) || curStepData.length === 0) {
        continue;
      }

      const startSubIndex = stepIndex === curStepIndex
        ? Math.min(curSubIndex - 1, curStepData.length - 1)
        : curStepData.length - 1;

      for (let subIndex = startSubIndex; subIndex >= 0; subIndex--) {
        if (!this.hasStepItem(curStepData[subIndex])) {
          continue;
        }

        return {
          data: curStepData,
          descriptor,
          index: stepIndex,
          subIndex,
        };
      }
    }

    return null;
  }

  private hasStepItem(item: any) {
    if (Array.isArray(item)) {
      return item.length > 0;
    }
    return !!item;
  }

  private getDescriptorItemsCount(descriptor: StepDescriptor) {
    const items = this.data?.[descriptor.key];
    if (!Array.isArray(items)) {
      return 0;
    }

    let total = 0;
    for (let i = 0; i < items.length; i++) {
      if (this.hasStepItem(items[i])) {
        total++;
      }
    }

    return total;
  }

  getTotalLessonItemsCount() {
    if (!this.data) {
      return 0;
    }

    let total = 0;
    for (let i = 0; i < stepDescriptors.length; i++) {
      total += this.getDescriptorItemsCount(stepDescriptors[i]);
    }

    return total;
  }

  getCompletedLessonItemsCount() {
    if (!this.data) {
      return 0;
    }

    if (this.lessonCompleted$.actual) {
      return this.getTotalLessonItemsCount();
    }

    const curStep = this.curStep$.actual;
    if (!curStep) {
      return 0;
    }

    let completed = 0;
    for (let i = 0; i < stepDescriptors.length; i++) {
      const descriptor = stepDescriptors[i];
      const items = this.data?.[descriptor.key];
      if (!Array.isArray(items)) {
        continue;
      }

      if (i < curStep.index) {
        completed += this.getDescriptorItemsCount(descriptor);
        continue;
      }

      if (i > curStep.index) {
        break;
      }

      const maxSubIndex = Math.max(0, Number(curStep.subIndex || 0));
      for (let j = 0; j < maxSubIndex && j < items.length; j++) {
        if (this.hasStepItem(items[j])) {
          completed++;
        }
      }
    }

    return completed;
  }

  getLessonProgressPercent() {
    const total = this.getTotalLessonItemsCount();
    if (!total) {
      return 0;
    }

    const completed = this.getCompletedLessonItemsCount();
    return Math.max(0, Math.min(100, Math.round((completed / total) * 100)));
  }

  canGoBack() {
    if (!this.data) {
      return false;
    }

    if (this.lessonCompleted$.actual) {
      return this.getTotalLessonItemsCount() > 0;
    }

    const curStep = this.curStep$.actual;
    const curStage = this.curStage$.actual;
    if (!curStep || !curStage) {
      return false;
    }

    return curStage.index > 0 || !!this.findPrevStep(this.data, curStep.index, curStep.subIndex);
  }

  restartLesson(): void {
    if (!this.data) {
      return;
    }

    const bookId = this.lessonProgressBookId;
    const exerciseId = this.lessonProgressExerciseId;

    void playerService.stopCurrentPlayer();

    this.lessonCompleted$.update(false);
    this.resetGrammarRuleBackStack();
    this.curStep$.update(null);
    this.curStage$.update(null);
    this.curStepItemIndex$.update(1);

    if (bookId && exerciseId) {
      clearLessonProgressFromStorage(bookId, exerciseId);
      void deleteLessonProgressOnServer(bookId, exerciseId);
      this.notifyLessonProgressChanged();
    }

    this.updateStep();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  updateStep() {
    const curStep = this.curStep$.actual;
    const step = appService.findNextStep(
      this.data,
      curStep ? curStep.index : -1,
      curStep ? curStep.subIndex : -1,
    );

    if (!step) {
      appService.curStep$.update(null);
      appService.curStepItemIndex$.update(0);
      appService.curStage$.update(null);
      appService.lessonCompleted$.update(true);
      this.persistLessonProgressSnapshot();
      return;
    }

    appService.lessonCompleted$.update(false);
    appService.curStep$.update(step);
    appService.curStepItemIndex$.update(1);

    const stages = step.descriptor.stages;
    if (stages?.length) {
      appService.curStage$.update({ index: 0, descriptor: stages[0] });
    } else {
      appService.curStage$.update(null);
    }

    this.persistLessonProgressSnapshot();
  }

  updateStage() {
    const curStep = appService.curStep$.actual;
    if (!curStep?.descriptor) {
      return;
    }

    const stage = appService.findNextStage(
      curStep.descriptor,
      appService.curStage$.actual ? appService.curStage$.actual.index : -1,
    );

    if (!stage.descriptor) {
      this.updateStep();
      return;
    }

    appService.curStage$.update(stage);
    this.persistLessonProgressSnapshot();
  }

  disableAutoPlay() {
    this.autoPlayOnCorrect$.update(false);
    void pushUserSettingsIfAuthorized();
  }

  updatePrevStage() {
    if (!this.data) {
      return;
    }

    if (this.lessonCompleted$.actual || !this.curStep$.actual) {
      const prevStep = this.findPrevStep(this.data, stepDescriptors.length - 1, Number.MAX_SAFE_INTEGER);
      if (!prevStep) {
        return;
      }

      appService.lessonCompleted$.update(false);
      appService.curStep$.update(prevStep);
      appService.curStepItemIndex$.update(1);

      const stages = prevStep.descriptor.stages || [];
      const prevStageIndex = Math.max(0, stages.length - 1);
      appService.curStage$.update({ index: prevStageIndex, descriptor: stages[prevStageIndex] });
      this.persistLessonProgressSnapshot();
      return;
    }

    const curStage = this.curStage$.actual;
    const curStep = this.curStep$.actual;
    if (curStage && curStage.index > 0 && curStep) {
      const prevStageIndex = curStage.index - 1;
      appService.curStage$.update({
        index: prevStageIndex,
        descriptor: curStep.descriptor.stages?.[prevStageIndex],
      });
      this.persistLessonProgressSnapshot();
      return;
    }

    const prevStep = this.findPrevStep(this.data, curStep.index, curStep.subIndex);
    if (!prevStep) {
      return;
    }

    appService.curStep$.update(prevStep);
    appService.curStepItemIndex$.update(1);

    const stages = prevStep.descriptor.stages || [];
    const prevStageIndex = Math.max(0, stages.length - 1);
    appService.curStage$.update({ index: prevStageIndex, descriptor: stages[prevStageIndex] });
    this.persistLessonProgressSnapshot();
  }

  toggleAutoPlayOnCorrect() {
    this.autoPlayOnCorrect$.update(!this.autoPlayOnCorrect$.actual);
    void pushUserSettingsIfAuthorized();
  }

  getCorrectAdvanceDelayMs() {
    return this.autoPlayOnCorrect$.actual ? 500 : 1000;
  }

  runAfterCorrect(path: string | null | undefined, done: () => void) {
    const finalize = () => {
      done();
    };

    if (!this.autoPlayOnCorrect$.actual) {
      setTimeout(finalize, 1000);
      return;
    }

    if (!path) {
      setTimeout(finalize, 500);
      return;
    }

    void playerService.playAudioAndWait(path).then((ok) => {
      if (ok) {
        finalize();
        return;
      }
      this.disableAutoPlay();
      finalize();
    });
  }
}

export const appService = new AppService();
